import './ArmyBlock.css'
import MissileIcon from './MissileIcon'
import DropZoneMarker, { ParachuteCanopy } from './DropZoneMarker'
import { COLOR } from '../../config/uiTokens'

// ─── ArmyBlock ────────────────────────────────────────────────────────────────
//
// One unit token's tile. Tiles are direction-neutral: a missile never displays
// its corridor's Plan — Plan state lives ONLY on the corridor dial at the
// center line. The upward chevron arrow on attacking tiles is an ATTACK
// indicator (aimed at the enemy), not a Plan direction.
//
// Props:
//   side           'player' | 'opp'
//   kind           'vet' | 'moved' | 'green' | 'base' | 'baseCon'
//   attacking      bool — corridor is attacking (gradient circle, upward chevron)
//   delivered      bool — resupply-delivered missile (parachute badge)
//   progress       number — baseCon only: rounds of attacks survived (drives
//                  the maturation loading bar; matures at FORT_MATURATION_ROUNDS)
//   movedFromSide  'left' | 'right' — origin-side stripe for moved-in tiles
//   dots           array of { side: 'top'|'bottom'|'left'|'right', color } —
//                  micro-dots hinting the tile's radial actions
//   gradientId     unique string — prevents SVG gradient/pattern id conflicts
//   onClick        fn — opens the tile's radial menu (player side only)
//

// ─── Fort icon — three drones side by side ───────────────────────────────────
// A Base is a cluster of drones, drawn as three drone glyphs. The glyph is
// square, so the cluster's units run smaller (11) than a lone unit's (15) to
// keep the three-wide footprint inside a tile. `size` only sets the spacing.
function FortMissiles({ size = 28, color = '#000000', down = false }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: size * 0.07, transform: down ? 'rotate(180deg)' : undefined }}>
            <MissileIcon size={11} color={color} />
            <MissileIcon size={11} color={color} />
            <MissileIcon size={11} color={color} />
        </div>
    )
}

// ─── Self resupply badge — black parachute glyph on a top corner ─────────────
// The circle tiles leave the corner badge floating on open land, so it wears
// its side's identity stroke (new-unit yellow here, opp red on theirs) to
// stay legible off the tile.
function SelfResupplyBadge() {
    return (
        <DropZoneMarker
            size={13}
            color="#000000"
            style={{
                position: 'absolute', top: '13%', right: '13%', zIndex: 5,
                stroke: '#f0e040', strokeWidth: 90, paintOrder: 'stroke', strokeLinejoin: 'round',
            }}
        />
    )
}

// ─── Maturation loading bar ──────────────────────────────────────────────────
// Sits just above the parachute on a Base in construction: 1% green at build,
// 51% after surviving one round of attacks. Gone once the Base matures.
function MaturationBar({ progress = 0, style }) {
    const pct = Math.min(100, 1 + progress * 50)
    return (
        <div
            style={{
                width: 20,
                height: 4,
                border: '1px solid #000000',
                borderRadius: 2,
                background: 'rgba(0,0,0,0.18)',
                overflow: 'hidden',
                ...style,
            }}
        >
            <div
                style={{
                    width: `${pct}%`,
                    height: '100%',
                    background: COLOR.BASE_PROGRESS,
                }}
            />
        </div>
    )
}

// ─── Base under construction — parachute over the missile cluster ────────────
// The canopy above the Base's three missiles says its 3rd (resupply) missile is
// inbound; it matures the Base once construction survives enough rounds. The
// loading bar above the canopy tracks that progress.
function BaseInConstruction({ color = '#000000', down = false, progress = 0 }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            {/* Extra clearance below the bar so it floats near the middle of
                the gap between the tile's top edge and the canopy. */}
            <MaturationBar progress={progress} style={{ marginBottom: 4 }} />
            <ParachuteCanopy width={20} color={color} />
            <FortMissiles size={22} color={color} down={down} />
        </div>
    )
}

// ─── Micro-dots ───────────────────────────────────────────────────────────────
function MicroDots({ dots }) {
    if (!dots?.length) return null
    return (
        <div className="ab__dots">
            {dots.map((d, i) => (
                <span key={i} className={`ab__dot ab__dot--${d.side}`} style={{ background: d.color }} />
            ))}
        </div>
    )
}

// ─── Tile backgrounds ─────────────────────────────────────────────────────────

// Attacking tile: the same CIRCLE as every other state (the old pentagon was
// an artifact of the square-tile build), filled with the red→amber attack
// gradient. The upward arrow is a flat-red cap: everything above the
// trapezoidal roofline is solid red, gradient below — the line sits low on
// the circle so the peak stays readable instead of losing its corners to the
// round edge.
function AttackSvg({ gradientId }) {
    const gradId = `atk_${gradientId}`
    return (
        <svg className="ab__svg-bg ab__svg-bg--round" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
                {/* red at bottom (y1=1), amber at top (y2=0) */}
                <linearGradient id={gradId} x1="0.5" y1="1" x2="0.5" y2="0">
                    <stop offset="0%"   stopColor={COLOR.GRAD_RED}         />
                    <stop offset="100%" stopColor={COLOR.GRAD_AMBER_LIGHT} />
                </linearGradient>
            </defs>
            <rect x="0" y="0" width="100" height="100" fill={`url(#${gradId})`} />
            <polygon points="0,0 0,46 50,16 100,46 100,0" fill={COLOR.BTN_ATTACK} />
        </svg>
    )
}

// Flat direction-neutral tile — a CIRCLE, so the land beneath the drone stays
// readable — with an optional origin-side stripe band for moved-in drones
// (the stripe marks where it came from, not where it goes; the circle clips
// it to a crescent).
function FlatSvg({ fill, stripeSide = null, patternId }) {
    const stripeId = `stripe_${patternId}`
    return (
        <svg className="ab__svg-bg ab__svg-bg--round" viewBox="0 0 100 100" preserveAspectRatio="none">
            {stripeSide && (
                <defs>
                    <pattern id={stripeId} patternUnits="userSpaceOnUse" x="0" y="0" width="100" height="10">
                        <rect x="0" y="0" width="100" height="5" fill={COLOR.LEAD_STROKE} />
                        <rect x="0" y="5" width="100" height="5" fill="#000000" />
                    </pattern>
                </defs>
            )}
            <rect className="ab__flat-fill" x="0" y="0" width="100" height="100" fill={fill} />
            {stripeSide === 'left'  && <rect x="0"  y="0" width="22" height="100" fill={`url(#${stripeId})`} />}
            {stripeSide === 'right' && <rect x="78" y="0" width="22" height="100" fill={`url(#${stripeId})`} />}
        </svg>
    )
}

// The drone glyph on a round tile, sized off the tile box like the circle
// itself (80cqmin): about 72% of the circle's width, so the wasp fills it.
// A new drone (yellow) or a landed airdrop wears the folded wasp; veterans
// the spread one.
const TILE_ICON = '58cqmin'

export default function ArmyBlock({
    side = 'player',
    kind = 'vet',
    attacking = false,
    delivered = false,
    progress = 0,
    movedFromSide = null,
    dots = null,
    gradientId,
    onClick,
}) {
    // ── Opponent tiles ────────────────────────────────────────────────────────
    if (side === 'opp') {
        if (kind === 'base' || kind === 'baseCon') {
            return (
                <div className="ab__block ab__block--opp">
                    <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center' }}>
                        {kind === 'baseCon'
                            ? <BaseInConstruction color={COLOR.ICON_OPP} down progress={progress} />
                            : <FortMissiles size={25} color={COLOR.ICON_OPP} down />
                        }
                    </div>
                </div>
            )
        }
        // A fresh opp deploy is NEW before it is theirs: yellow on the
        // ground like yours (.ab__block--opp-new), black glyph so it reads on
        // the yellow; it turns the opp red as it lifts off into a veteran
        // (HexBoard.css .hexb__lifting). An AIRDROPPED drone keeps its own
        // look: the red→yellow gradient with the parachute badge.
        const fresh = kind === 'green' && !delivered
        return (
            <div className={`ab__block ab__block--opp${fresh ? ' ab__block--opp-new' : ''}${delivered ? ' ab__block--opp-resupply' : ''}`}>
                <MissileIcon className="ab__tile-wasp" size={TILE_ICON} color={fresh ? '#000000' : COLOR.ICON_OPP} pose={kind === 'green' || delivered ? 'folded' : 'spread'}
                    style={{ transform: 'rotate(180deg)' }} />
                {delivered && (
                    // Resupply arrival badge — floats off the circle's rim, so
                    // it wears the opp identity's red stroke to stay legible.
                    <DropZoneMarker
                        size={13}
                        color={COLOR.ICON_OPP}
                        style={{
                            position: 'absolute', top: '13%', right: '13%', zIndex: 1,
                            stroke: '#C04040', strokeWidth: 90, paintOrder: 'stroke', strokeLinejoin: 'round',
                        }}
                    />
                )}
            </div>
        )
    }

    // ── Player tiles ──────────────────────────────────────────────────────────
    const isBase = kind === 'base' || kind === 'baseCon'

    const bg = attacking
        ? <AttackSvg gradientId={gradientId} />
        : kind === 'green'   ? <FlatSvg fill={COLOR.GRAD_NEW}    patternId={gradientId} />
        : kind === 'baseCon' ? <FlatSvg fill={COLOR.GRAD_NEW}    patternId={gradientId} />
        : kind === 'moved'   ? <FlatSvg fill={COLOR.ARMY_PLAYER} stripeSide={movedFromSide} patternId={gradientId} />
        :                      <FlatSvg fill={COLOR.ARMY_PLAYER} patternId={gradientId} />

    const icon = kind === 'base'    ? <FortMissiles size={28} color="#000000" />
               : kind === 'baseCon' ? <BaseInConstruction progress={progress} />
               :                      <MissileIcon className="ab__tile-wasp" size={TILE_ICON} color="#000000" pose={(kind === 'green' || delivered) && !attacking ? 'folded' : 'spread'} />

    return (
        <div
            className="ab__wrap ab__wrap--round"
            onClick={onClick}
            style={onClick ? undefined : { cursor: 'default' }}
        >
            {bg}
            <div className="ab__content">{icon}</div>
            {delivered && !isBase && <SelfResupplyBadge />}
            <MicroDots dots={dots} />
        </div>
    )
}
